import React, { useState } from 'react';
import { doc, deleteDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import {
  DEFAULT_SHARE_TEMPLATE,
  toDayName,
  toGregorianArabic,
  toHijriDate,
  buildShareMessage,
  openWhatsApp,
  buildGoogleCalendarUrl
} from '../../utils/whatsapp';
import './BookingModal.css';

export default function BookingDetailsModal({ booking, calendar, onClose, onDeleted }) {
  const { currentUser, userRole } = useAuth();
  const { showToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [showShare, setShowShare] = useState(false);
  const [shareMessage, setShareMessage] = useState('');
  const [sharePassword, setSharePassword] = useState('');
  const [sharePasswordError, setSharePasswordError] = useState('');
  const [shareUnlocked, setShareUnlocked] = useState(false);

  // Check permissions
  const isMyBooking = currentUser && booking.bookedBy === currentUser.uid;
  const isAdmin = currentUser && calendar.createdBy === currentUser.uid;
  const isSuperAdmin = currentUser && userRole === 'superAdmin';
  const hasPassword = !!booking.deletePassword;
  const canDelete = isAdmin || isSuperAdmin || isMyBooking || hasPassword;
  const canShare = isMyBooking || isAdmin || isSuperAdmin || hasPassword;
  // Needs password to unlock share (guest with password, not admin/owner)
  const shareNeedsPassword = hasPassword && !isMyBooking && !isAdmin && !isSuperAdmin;

  // Format date for display
  const arabicMonths = [
    'يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو',
    'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر'
  ];

  // Use YYYY-MM-DD safe parsing to avoid timezone issues
  const dateStr = booking.date.includes('T') ? booking.date.split('T')[0] : booking.date;
  const [dateYear, dateMonth, dateDay] = dateStr.split('-').map(Number);
  const day = dateDay;
  const month = arabicMonths[dateMonth - 1];
  const year = dateYear;

  function handleToggleShare() {
    if (!showShare && !shareNeedsPassword && !shareMessage) {
      const msg = buildShareMessage(calendar.shareTemplate || DEFAULT_SHARE_TEMPLATE, {
        dayName: toDayName(dateStr),
        gregorianDate: toGregorianArabic(dateStr),
        hijriDate: toHijriDate(dateStr),
        note: booking.note || ''
      });
      setShareMessage(msg);
    }
    setShowShare(!showShare);
  }

  function handleUnlockShare() {
    if (sharePassword !== booking.deletePassword) {
      setSharePasswordError('كلمة المرور غير صحيحة');
      return;
    }
    const msg = buildShareMessage(calendar.shareTemplate || DEFAULT_SHARE_TEMPLATE, {
      dayName: toDayName(dateStr),
      gregorianDate: toGregorianArabic(dateStr),
      hijriDate: toHijriDate(dateStr)
    });
    setShareMessage(msg);
    setShareUnlocked(true);
    setSharePasswordError('');
  }

  async function handleDelete() {
    if (hasPassword && !isAdmin && !isSuperAdmin) {
      if (deletePassword !== booking.deletePassword) {
        setPasswordError('كلمة المرور غير صحيحة');
        return;
      }
    }

    try {
      setLoading(true);
      setPasswordError('');
      await deleteDoc(doc(db, 'bookings', booking.id));
      onDeleted();
    } catch (error) {
      console.error('Error deleting booking:', error);
      showToast('حدث خطأ أثناء حذف الحجز', 'error');
      setLoading(false);
    }
  }

  if (showConfirmDelete) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="booking-modal" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h2>تأكيد الحذف</h2>
            <button className="close-btn" onClick={onClose}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          </div>

          <div className="confirm-delete-content">
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2">
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="15" y1="9" x2="9" y2="15"></line>
              <line x1="9" y1="9" x2="15" y2="15"></line>
            </svg>
            <h3>هل أنت متأكد؟</h3>
            <p>سيتم حذف حجز "{booking.guestName}" بشكل نهائي.</p>

            {hasPassword && !isAdmin && !isSuperAdmin && (
              <div className="form-group" style={{ marginTop: '16px', textAlign: 'right' }}>
                <label htmlFor="deletePassword">كلمة مرور الحذف</label>
                <input
                  id="deletePassword"
                  type="password"
                  value={deletePassword}
                  onChange={(e) => {
                    setDeletePassword(e.target.value);
                    setPasswordError('');
                  }}
                  placeholder="أدخل كلمة المرور"
                  disabled={loading}
                />
                {passwordError && <div className="error-alert" style={{ marginTop: '8px' }}>{passwordError}</div>}
              </div>
            )}
          </div>

          <div className="modal-actions">
            <button
              className="btn btn-secondary"
              onClick={() => setShowConfirmDelete(false)}
              disabled={loading}
            >
              إلغاء
            </button>
            <button
              className="btn btn-danger"
              onClick={handleDelete}
              disabled={loading}
            >
              {loading ? 'جاري الحذف...' : 'حذف الحجز'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="booking-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>تفاصيل الحجز</h2>
          <button className="close-btn" onClick={onClose}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>

        <div className="booking-date-display">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
            <line x1="16" y1="2" x2="16" y2="6"></line>
            <line x1="8" y1="2" x2="8" y2="6"></line>
            <line x1="3" y1="10" x2="21" y2="10"></line>
          </svg>
          <div className="date-text">
            <span className="date-day">{day}</span>
            <span className="date-month-year">{month} {year}</span>
          </div>
        </div>

        <div className="booking-info">
          <div className="info-row">
            <span className="info-label">محجوز بواسطة:</span>
            <span className="info-value">{booking.guestName}</span>
          </div>
          <div className="info-row">
            <span className="info-label">تاريخ الحجز:</span>
            <span className="info-value">
              {new Date(booking.createdAt).toLocaleDateString('ar-SA-u-ca-gregory')}
            </span>
          </div>
          {booking.note && (
            <div className="info-row">
              <span className="info-label">المناسبة:</span>
              <span className="info-value">{booking.note}</span>
            </div>
          )}
          {isMyBooking && (
            <div className="my-booking-badge">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                <polyline points="22 4 12 14.01 9 11.01"></polyline>
              </svg>
              <span>هذا حجزك</span>
            </div>
          )}
        </div>

        {/* Google Calendar button - visible to everyone */}
        <a
          className="btn-google-calendar"
          href={buildGoogleCalendarUrl({ date: dateStr, calendarName: calendar.name, guestName: booking.guestName, note: booking.note || '' })}
          target="_blank"
          rel="noopener noreferrer"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
            <line x1="16" y1="2" x2="16" y2="6"></line>
            <line x1="8" y1="2" x2="8" y2="6"></line>
            <line x1="3" y1="10" x2="21" y2="10"></line>
          </svg>
          أضف إلى تقويم Google
        </a>

        {/* WhatsApp share section */}
        {canShare && (
          <div className="share-details-section">
            <button className="btn-share-toggle" onClick={handleToggleShare}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
              {showShare ? 'إخفاء المشاركة' : 'مشاركة الحجز'}
              <svg
                className={`share-chevron ${showShare ? 'open' : ''}`}
                width="14" height="14" viewBox="0 0 24 24"
                fill="none" stroke="currentColor" strokeWidth="2.5"
              >
                <polyline points="6 9 12 15 18 9"></polyline>
              </svg>
            </button>

            {showShare && shareNeedsPassword && !shareUnlocked && (
              <div className="share-section">
                <label className="share-label">أدخل كلمة المرور لمشاركة الحجز</label>
                <input
                  type="password"
                  className="share-password-input"
                  value={sharePassword}
                  onChange={(e) => { setSharePassword(e.target.value); setSharePasswordError(''); }}
                  placeholder="كلمة مرور الحجز"
                  onKeyDown={(e) => e.key === 'Enter' && handleUnlockShare()}
                />
                {sharePasswordError && (
                  <div className="error-alert" style={{ marginTop: '8px' }}>{sharePasswordError}</div>
                )}
                <button className="btn btn-primary" style={{ marginTop: '10px', width: '100%' }} onClick={handleUnlockShare}>
                  تأكيد
                </button>
              </div>
            )}

            {showShare && (!shareNeedsPassword || shareUnlocked) && (
              <div className="share-section">
                <label className="share-label">يمكنك تعديل الرسالة قبل الإرسال</label>
                <textarea
                  className="share-message-textarea"
                  value={shareMessage}
                  onChange={(e) => setShareMessage(e.target.value)}
                  rows={7}
                  dir="rtl"
                />
                <button
                  className="btn-whatsapp"
                  onClick={() => openWhatsApp(shareMessage)}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                  إرسال عبر واتساب
                </button>
              </div>
            )}
          </div>
        )}

        <div className="modal-actions">
          <button className="btn btn-secondary" onClick={onClose}>
            إغلاق
          </button>
          {canDelete && (
            <button
              className="btn btn-danger"
              onClick={() => setShowConfirmDelete(true)}
            >
              حذف الحجز
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
